// src/components/UniversalDocumentViewer.jsx
"use client";
import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Stack, Typography, Button, Tooltip, Box, LinearProgress,
  Select, MenuItem
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import RotateRightIcon from "@mui/icons-material/RotateRight";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DownloadIcon from "@mui/icons-material/Download";

import * as XLSX from "xlsx";
import { DataGrid } from "@mui/x-data-grid";
import * as mammoth from "mammoth";
import hljs from "highlight.js";

import { Document, Page, pdfjs } from "react-pdf";
// pdfjs.GlobalWorkerOptions.workerSrc = new URL(
//   "pdf.worker.min.js",
//   import.meta.url.replace(/\/[^/]*$/, "/") + "pdf.worker.min.js"
// ).toString();

// pdfjs.GlobalWorkerOptions.workerSrc =
//    `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

import "pdfjs-dist/build/pdf.worker.entry";

const isPdf = (ct = "", url = "") =>
  /application\/pdf/i.test(ct) || /\.pdf(\?|$)/i.test(url);
const isImage = (ct = "", url = "") =>
  /^image\//i.test(ct) || /\.(png|jpe?g|gif|webp|avif|bmp|svg)(\?|$)/i.test(url);
const isCsv = (ct = "", url = "") =>
  /(text\/csv|application\/csv)/i.test(ct) || /\.csv(\?|$)/i.test(url);
const isExcel = (ct = "", url = "") =>
  /(application\/vnd\.ms-excel|application\/vnd\.openxmlformats.*sheet)/i.test(ct) ||
  /\.(xls|xlsx)(\?|$)/i.test(url);
const isDocx = (ct = "", url = "") =>
  /application\/vnd\.openxmlformats.*wordprocessingml\.document/i.test(ct) || /\.docx(\?|$)/i.test(url);
const isPlainTextLike = (ct = "", url = "") =>
  /(text\/plain|application\/json|application\/xml|text\/(yaml|yml|markdown|md|xml|html))/i.test(ct) ||
  /\.(txt|json|ya?ml|md|xml|log|conf|ini|csv|tsv)(\?|$)/i.test(url);
const isAudio = (ct = "", url = "") =>
  /^audio\//i.test(ct) || /\.(mp3|ogg|wav|m4a|flac)(\?|$)/i.test(url);
const isVideo = (ct = "", url = "") =>
  /^video\//i.test(ct) || /\.(mp4|webm|ogv|mov|m4v)(\?|$)/i.test(url);
const isHtmlDoc = (ct = "", url = "") =>
  /text\/html/i.test(ct) || /\.html?(\?|$)/i.test(url);

export default function UniversalDocumentViewer({
  open,
  onClose,
  fileUrl,          // presigned or preview URL (same-origin not required)
  fileName,
  contentType,      // mime from server when possible
}) {
  const [scale, setScale] = useState(1.0);
  const [rotate, setRotate] = useState(0);
  const [numPages, setNumPages] = useState(null);
  const [page, setPage] = useState(1);

  // Sheet state
  const [wb, setWb] = useState(null);
  const [sheetNames, setSheetNames] = useState([]);
  const [activeSheet, setActiveSheet] = useState("");
  const [rows, setRows] = useState([]);
  const [cols, setCols] = useState([]);

  // DOCX -> HTML
  const [docxHtml, setDocxHtml] = useState("");

  // Plain text/code
  const [plainText, setPlainText] = useState("");

  // Loading
  const [loading, setLoading] = useState(false);

  const canPdf   = isPdf(contentType, fileUrl);
  const canImg   = isImage(contentType, fileUrl);
  const canCsv   = isCsv(contentType, fileUrl);
  const canExcel = isExcel(contentType, fileUrl);
  const canDocx  = isDocx(contentType, fileUrl);
  const canText  = isPlainTextLike(contentType, fileUrl);
  const canAudio = isAudio(contentType, fileUrl);
  const canVideo = isVideo(contentType, fileUrl);
  const canHtml  = isHtmlDoc(contentType, fileUrl);

  useEffect(() => {
    if (!open) return;
    setScale(1); setRotate(0); setPage(1);
    setWb(null); setSheetNames([]); setActiveSheet(""); setRows([]); setCols([]);
    setDocxHtml(""); setPlainText("");

    const load = async () => {
      // For types we render with JS libs, fetch Blob/ArrayBuffer
      try {
        if (canExcel || canCsv) {
          setLoading(true);
          const resp = await fetch(fileUrl);
          const buf = await resp.arrayBuffer();
          const book = XLSX.read(buf, { type: "array" });
          setWb(book);
          setSheetNames(book.SheetNames || []);
          const first = (book.SheetNames || [])[0];
          if (first) loadSheet(book, first);
        } else if (canDocx) {
          setLoading(true);
          const resp = await fetch(fileUrl);
          const buf = await resp.arrayBuffer();
          const result = await mammoth.convertToHtml({ arrayBuffer: buf }, { includeDefaultStyleMap: true });
          setDocxHtml(result.value || "<p>(Empty)</p>");
        } else if (canText) {
          setLoading(true);
          const resp = await fetch(fileUrl);
          const text = await resp.text();
          setPlainText(text);
        }
      } catch {
        // ignore, fallback will still show open/download
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, fileUrl]);

  const loadSheet = (book, name) => {
    const ws = book.Sheets[name];
    const json = XLSX.utils.sheet_to_json(ws, { header: 1, raw: true });
    // Build DataGrid columns from first row
    const headers = (json[0] || []).map((h, i) => ({
      field: `c${i}`, headerName: String(h ?? `Column ${i+1}`), flex: 1, minWidth: 120
    }));
    const dataRows = (json.slice(1) || []).map((r, idx) => {
      const obj = { id: idx + 1 };
      headers.forEach((c, i) => { obj[c.field] = r[i] ?? ""; });
      return obj;
    });
    setCols(headers.length ? headers : [{ field:"c0", headerName:"Column 1", flex:1, minWidth:120 }]);
    setRows(dataRows.length ? dataRows : [{ id:1, c0:"(empty)" }]);
    setActiveSheet(name);
  };

  const zoomIn  = () => setScale((s) => Math.min(4, +(s + 0.2).toFixed(2)));
  const zoomOut = () => setScale((s) => Math.max(0.4, +(s - 0.2).toFixed(2)));
  const reset   = () => { setScale(1); setRotate(0); };
  const next    = () => setPage((p) => Math.min((numPages || 1), p + 1));
  const prev    = () => setPage((p) => Math.max(1, p - 1));

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg" PaperProps={{ sx: { height: "92vh" } }}>
      <DialogTitle sx={{ pr: 1 }}>
        {fileName || "Document"}
        <Stack direction="row" spacing={0.5} sx={{ float: "right" }}>
          <Tooltip title="Open in new tab">
            <IconButton onClick={() => window.open(fileUrl, "_blank", "noopener")}>
              <OpenInNewIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Download">
            <IconButton component="a" href={fileUrl} download={fileName || true}>
              <DownloadIcon />
            </IconButton>
          </Tooltip>
          <IconButton onClick={onClose}><CloseIcon /></IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0, display: "flex", flexDirection: "column" }}>
        {/* Toolbar */}
        <Stack direction="row" spacing={1} alignItems="center" sx={{ p: 1.25, borderBottom: "1px solid", borderColor: "divider" }}>
          <Tooltip title="Zoom out"><span><IconButton onClick={zoomOut} disabled={scale <= 0.4}><ZoomOutIcon/></IconButton></span></Tooltip>
          <Typography variant="body2" sx={{ minWidth: 64, textAlign: "center" }}>{Math.round(scale * 100)}%</Typography>
          <Tooltip title="Zoom in"><span><IconButton onClick={zoomIn} disabled={scale >= 4}><ZoomInIcon/></IconButton></span></Tooltip>
          <Tooltip title="Rotate 90°"><IconButton onClick={() => setRotate((r) => (r + 90) % 360)}><RotateRightIcon/></IconButton></Tooltip>
          <Tooltip title="Reset"><IconButton onClick={reset}><RestartAltIcon/></IconButton></Tooltip>
          <Box sx={{ flexGrow: 1 }} />
          {canPdf && (
            <Stack direction="row" spacing={1} alignItems="center">
              <IconButton onClick={prev} disabled={page <= 1}><ArrowBackIcon/></IconButton>
              <Typography variant="body2">Page {page}{numPages ? ` / ${numPages}` : ""}</Typography>
              <IconButton onClick={next} disabled={numPages ? page >= numPages : true}><ArrowForwardIcon/></IconButton>
            </Stack>
          )}
          {canExcel && sheetNames.length > 1 && (
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2">Sheet:</Typography>
              <Select
                size="small"
                value={activeSheet || ""}
                onChange={(e) => loadSheet(wb, e.target.value)}
              >
                {sheetNames.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </Select>
            </Stack>
          )}
        </Stack>

        {/* Body */}
        <Box sx={{ flex: 1, overflow: "auto", display: "grid", placeItems: "center", bgcolor: "background.default", p: 2 }}>
          {loading && (
            <Box sx={{ width: 360, p: 2 }}>
              <LinearProgress />
              <Typography variant="caption">Loading…</Typography>
            </Box>
          )}

          {!loading && canPdf && (
            <Box sx={{ transform: `scale(${scale}) rotate(${rotate}deg)`, transformOrigin: "top center" }}>
              <Document
                file={fileUrl}
                onLoadSuccess={({ numPages }) => setNumPages(numPages)}
                loading={<LinearProgress />}
                onLoadError={() => {}}
              >
                <Page pageNumber={page} renderTextLayer={false} renderAnnotationLayer={false} />
              </Document>
            </Box>
          )}

          {!loading && canImg && (
            <img
              src={fileUrl}
              alt={fileName || "image"}
              style={{
                transform: `scale(${scale}) rotate(${rotate}deg)`,
                transformOrigin: "center center",
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
                display: "block"
              }}
            />
          )}

          {!loading && (canCsv || canExcel) && (
            <Box sx={{ width: "100%", height: "100%", px: 2 }}>
              <div style={{ height: "calc(100% - 8px)", width: "100%" }}>
                <DataGrid
                  rows={rows}
                  columns={cols}
                  pageSizeOptions={[25, 50, 100]}
                  initialState={{ pagination: { paginationModel: { pageSize: 50 } } }}
                  disableColumnMenu
                />
              </div>
            </Box>
          )}

          {!loading && canDocx && (
            <Box
              sx={{ width: "100%", maxWidth: 1100, p: 2, transform: `scale(${scale}) rotate(${rotate}deg)`, transformOrigin: "top center" }}
              dangerouslySetInnerHTML={{ __html: docxHtml }}
            />
          )}

          {!loading && canText && (
            <Box
              component="pre"
              sx={{
                width: "100%",
                maxWidth: 1200,
                p: 2,
                whiteSpace: "pre-wrap",
                transform: `scale(${scale}) rotate(${rotate}deg)`,
                transformOrigin: "top center",
                bgcolor: "background.paper",
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider"
              }}
              dangerouslySetInnerHTML={{
                __html: hljs.highlightAuto(plainText || "").value
              }}
            />
          )}

          {!loading && canAudio && (
            <audio controls style={{ width: "90%" }}>
              <source src={fileUrl} />
              Your browser does not support the audio element.
            </audio>
          )}

          {!loading && canVideo && (
            <video controls style={{ width: "90%", maxHeight: "80vh" }}>
              <source src={fileUrl} />
              Your browser does not support the video tag.
            </video>
          )}

          {!loading && canHtml && (
            <iframe
              src={fileUrl}
              title={fileName || "HTML"}
              style={{ width: "100%", height: "100%", border: 0 }}
              sandbox="allow-scripts allow-same-origin"
            />
          )}

          {!loading && !(
            canPdf || canImg || canCsv || canExcel || canDocx || canText || canAudio || canVideo || canHtml
          ) && (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: "center" }}>
              Preview not natively supported in browser. You can still open in a new tab or download via the buttons above.
            </Typography>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
